<template>
  <div class="central-content" :style="contentStyle">
    <!-- PRIORITY 1: Show Labeler if in labeler mode -->
    <WorkspaceLabeler v-if="labelerMode" />

    <!-- PRIORITY 2: Show DataAnnotations when image is selected (and not in labeler mode) -->
    <template v-else-if="selectedImage && selectedImageType">
      <!-- Prospective DataAnnotations -->
      <ProspectiveDataAnnotations
        v-if="selectedImageType === 'prospective'"
        :image="selectedImage"
        :project="selectedProject"
        :current-image-index="currentImageIndex"
        :total-images="totalImages"
        @closeBottom="handleCloseImage"
        @navigatePrevious="handleNavigatePrevious"
        @navigateNext="handleNavigateNext"
      />

      <!-- Retrospective DataAnnotations -->
      <RetroDataAnnotations
        v-else-if="selectedImageType === 'retrospective'"
        :image="selectedImage"
        :project="selectedProject"
        :current-study-index="currentImageIndex"
        :total-images="totalImages"
        @closeBottom="handleCloseImage"
        @navigatePrevious="handleNavigatePrevious"
        @navigateNext="handleNavigateNext"
      />
    </template>

    <!-- PRIORITY 3: Show default router view when no special view is active -->
    <template v-else>
      <slot />
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import ProspectiveDataAnnotations from './DataAnnotations/ProspectiveDataAnnotations.vue'
import RetroDataAnnotations from './DataAnnotations/RetroDataAnnotations.vue'
import WorkspaceLabeler from './Labeler/WorkspaceLabeler.vue'

const store = useStore()
const route = useRoute()

const isProjectSelectPage = computed(() => route.name === 'Workspace' || route.path === '/')

const selectedImage = computed(() => store.state.workspacePanel.selectedImage)
const selectedImageType = computed(() => store.state.workspacePanel.selectedImageType)
const currentImageIndex = computed(() => store.state.workspacePanel.imageIndex)
const totalImages = computed(() => store.state.workspacePanel.totalImages)
const labelerMode = computed(() => store.state.workspacePanel.labelerMode)

const selectedProjectId = computed(() => store.state.projectSubmission.selectedProjectId)
const projects = computed(() => store.state.projectSubmission.projects || [])
const selectedProject = computed(() =>
  projects.value.find(p => p._id === selectedProjectId.value)
)

const contentStyle = computed(() => {
  const rightMargin = store.getters['workspacePanel/rightPanelWidth']

  if (isProjectSelectPage.value) {
    const navWidth = store.state.workspacePanel.rightPanelCollapsed ? '70px' : '250px'
    return {
      marginLeft: navWidth,
      marginRight: rightMargin
    }
  }

  const leftMargin = store.getters['workspacePanel/leftPanelWidth']
  return {
    marginLeft: leftMargin,
    marginRight: rightMargin
  }
})

// Handle close button click
const handleCloseImage = () => {
  store.dispatch('workspacePanel/clearSelectedImage')
}

// Handle image navigation - communicate with LeftPanel
const handleNavigatePrevious = () => {
  // Emit a custom event or call a store action to navigate to previous image
  // This will be handled by the LeftPanel component
  window.dispatchEvent(new CustomEvent('navigatePreviousImage'))
}

const handleNavigateNext = () => {
  // Emit a custom event or call a store action to navigate to next image
  // This will be handled by the LeftPanel component
  window.dispatchEvent(new CustomEvent('navigateNextImage'))
}
</script>

<style scoped>
.central-content {
  height: calc(100vh - var(--header-height));
  margin-top: var(--header-height);
  overflow-y: auto;
  overflow-x: hidden;
  transition: all 0.3s ease;
  background-color: var(--color-bg-secondary);
}
</style>
